import type { PrimaryEntity } from '../PageConfigProvider/types/chart';
import type { DimensionSource } from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import type { PageState, SubfilterTree } from './types';

type SetPageStateAction = {
  type: 'SET_PAGE_STATE';
  payload: PageState;
};

type SetSelectionsAction = {
  type: 'SET_SELECTIONS';
  payload: {
    plotId: string;
    selections: SubfilterTree[];
    updateInitial?: boolean;
  };
};

type SetPrimaryEntityAction = {
  type: 'SET_PRIMARY_ENTITY';
  payload: {
    plotId: string;
    primaryEntity: PrimaryEntity;
  };
};

type SetMultiPrimaryEntitiesAction = {
  type: 'SET_MULTI_PRIMARY_ENTITIES';
  payload: {
    plotId: string;
    primaryEntities: PrimaryEntity[];
  };
};

type ClearPrimaryEntitiesAction = {
  type: 'CLEAR_PRIMARY_ENTITIES';
  payload: {
    plotIds: string[];
  };
};

type ClearMultiPrimaryEntitiesAction = {
  type: 'CLEAR_MULTI_PRIMARY_ENTITIES';
  payload: {
    plotIds: string[];
  };
};

type SetActiveTabAction = {
  type: 'SET_ACTIVE_TAB';
  payload: string;
};

type SetMetricOverrideAction = {
  type: 'SET_METRIC_OVERRIDE';
  payload: {
    plotId: string;
    metric: MetricValues;
  };
};

type SetDimensionSourceOverrideAction = {
  type: 'SET_DIMENSION_SOURCE_OVERRIDE';
  payload: {
    plotId: string;
    dimensionSource: DimensionSource;
  };
};

type ClearDimensionSourceOverrideAction = {
  type: 'CLEAR_DIMENSION_SOURCE_OVERRIDE';
  payload: {
    plotIds: string[];
  };
};

type ShareOfSegmentAction = SetSelectionsAction;

export type PageStateAction =
  | SetPageStateAction
  | ShareOfSegmentAction
  | SetActiveTabAction
  | SetMetricOverrideAction
  | SetPrimaryEntityAction
  | SetMultiPrimaryEntitiesAction
  | ClearPrimaryEntitiesAction
  | ClearMultiPrimaryEntitiesAction
  | SetDimensionSourceOverrideAction
  | ClearDimensionSourceOverrideAction;

export const pageStateReducer = (
  state: PageState,
  action: PageStateAction
): PageState => {
  switch (action.type) {
    case 'SET_PAGE_STATE': {
      return action.payload;
    }

    case 'SET_ACTIVE_TAB': {
      return {
        ...state,
        activeTabId: action.payload,
      };
    }
    case 'SET_SELECTIONS': {
      return {
        ...state,
        selections: {
          ...state.selections,
          [action.payload.plotId]: action.payload.selections,
        },
        initialSelections: {
          ...state.initialSelections,
          [action.payload.plotId]: action.payload.updateInitial
            ? action.payload.selections
            : (state.initialSelections[action.payload.plotId] ??
              action.payload.selections),
        },
      };
    }
    case 'SET_PRIMARY_ENTITY': {
      return {
        ...state,
        primaryEntities: {
          ...state.primaryEntities,
          [action.payload.plotId]: action.payload.primaryEntity,
        },
      };
    }
    case 'SET_MULTI_PRIMARY_ENTITIES': {
      return {
        ...state,
        multiPrimaryEntities: {
          ...state.multiPrimaryEntities,
          [action.payload.plotId]: action.payload.primaryEntities,
        },
      };
    }
    case 'CLEAR_PRIMARY_ENTITIES': {
      return {
        ...state,
        primaryEntities: action.payload.plotIds.reduce((acc, plotId) => {
          const { [plotId]: _, ...remainingPrimaryEntities } = acc;
          return remainingPrimaryEntities;
        }, state.primaryEntities),
      };
    }
    case 'CLEAR_MULTI_PRIMARY_ENTITIES': {
      return {
        ...state,
        multiPrimaryEntities: action.payload.plotIds.reduce((acc, plotId) => {
          const { [plotId]: _, ...remainingMultiPrimaryEntities } = acc;
          return remainingMultiPrimaryEntities;
        }, state.multiPrimaryEntities),
      };
    }
    case 'SET_METRIC_OVERRIDE': {
      return {
        ...state,
        metricOverrides: {
          ...state.metricOverrides,
          [action.payload.plotId]: action.payload.metric,
        },
      };
    }
    case 'SET_DIMENSION_SOURCE_OVERRIDE': {
      return {
        ...state,
        dimensionSourceOverrides: {
          ...state.dimensionSourceOverrides,
          [action.payload.plotId]: action.payload.dimensionSource,
        },
      };
    }
    case 'CLEAR_DIMENSION_SOURCE_OVERRIDE': {
      return {
        ...state,
        dimensionSourceOverrides: action.payload.plotIds.reduce(
          (acc, plotId) => {
            const { [plotId]: _, ...remainingDimensionSourceOverrides } = acc;
            return remainingDimensionSourceOverrides;
          },
          state.dimensionSourceOverrides
        ),
      };
    }
    default:
      return state;
  }
};
